import { useState } from 'react';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  useGetKybVerificationByIdQuery,
  useApproveKybVerificationMutation,
  useRejectKybVerificationMutation,
  useUpdateKybDocStatusMutation,
} from '../../reactWrapper/Redux/apiSlice';
import Status from '../../components/Status/Status';
import LoadingSpinner from '../../components/Loader/LoadingSpinner';
import PageIntro from '../../components/PageIntro/PageIntro';

/* The two hand-rolled colour maps (STATUS_COLORS / DOC_STATUS_COLORS) are gone:
   record status is always a StatusChip (rule 15), which already carries the
   semantic tints they were approximating. */

const DOC_LABELS = {
  identification_file: { label: 'Identification Document', key: 'identification_file', statusKey: 'identification_file_status', reasonKey: 'identification_reason' },
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
};

const DOC_ORDER_INDIVIDUAL = ['identification_file'];
const DOC_ORDER_BUSINESS = ['identification_file'];

const DetailItem = ({ label, value }) => (
  <div className="rounded-lg bg-surface px-3 py-2">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {label}
    </p>
    <p className="mt-0.5 text-sm font-medium text-brand-navy">{value || '—'}</p>
  </div>
);

export default function KybDetails() {
  const { id } = useParams();
  const { data: kyb, isLoading } = useGetKybVerificationByIdQuery(id);
  const [updateDocStatus] = useUpdateKybDocStatusMutation();
  const [verifyKyb] = useApproveKybVerificationMutation();
  const [rejectKyb] = useRejectKybVerificationMutation();
  const [rejectingKey, setRejectingKey] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showAdminNote, setShowAdminNote] = useState(false);

  const docs = kyb?.category === 'individual' ? DOC_ORDER_INDIVIDUAL : DOC_ORDER_BUSINESS;

  const approvedCount = docs.filter((docKey) => {
    const config = DOC_LABELS[docKey];
    return kyb?.[config.statusKey] === 'approved';
  }).length;
  const totalCount = docs.length;

  const kybCompleted = kyb?.status === 'completed';

  /* The one approval on this page. There used to be a second, a Verify button
     in the header that could only be pressed after every document here had
     been approved, so each KYB took two approvals. Approving the last
     outstanding document now verifies the KYB as well. The two are still
     separate API calls: `update-doc` only marks the document. */
  const handleApproveDoc = async (docKey) => {
    const alreadyApproved = kyb?.[DOC_LABELS[docKey].statusKey] === 'approved';
    const othersApproved = docs.every(
      (key) => key === docKey || kyb?.[DOC_LABELS[key].statusKey] === 'approved'
    );

    try {
      if (!alreadyApproved) {
        await updateDocStatus({ id, doc_key: docKey, action: 'approve' }).unwrap();
      }
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to approve document');
      return;
    }

    if (!othersApproved || kybCompleted) {
      toast.success('Document approved');
      return;
    }

    try {
      await verifyKyb(id).unwrap();
      toast.success('KYB approved');
    } catch (err) {
      toast.error(err?.data?.message || 'Document approved, but the KYB could not be verified');
    }
  };

  /* Likewise the one rejection: the header's Reject, which rejected the KYB
     with an admin note, is gone. Disapproving a document now rejects the KYB
     too, and the reason given doubles as the admin note the client sees. */
  const handleRejectDoc = async (docKey) => {
    const reason = rejectReason.trim();
    if (!reason) return;

    try {
      await updateDocStatus({ id, doc_key: docKey, action: 'reject', reason }).unwrap();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to reject document');
      return;
    }

    setRejectingKey(null);
    setRejectReason('');

    if (kybCompleted) {
      toast.success('Document rejected');
      return;
    }

    try {
      await rejectKyb({ id, admin_note: reason }).unwrap();
      toast.success('KYB rejected');
    } catch (err) {
      toast.error(err?.data?.message || 'Document rejected, but the KYB could not be rejected');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!kyb) {
    return (
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed bg-card px-6 py-14 text-center">
          <p className="text-lg font-semibold text-brand-navy">KYB record not found</p>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            This verification record may have been removed.
          </p>
        </div>
      </div>
    );
  }

  const companyName = kyb?.company?.name || kyb?.legal_name || 'N/A';
  const companyLogo = kyb?.company?.logo;
  const companyEmail = kyb?.user?.email || kyb?.company?.email || '';

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageIntro title="KYB Details" description={companyName} />

      {/* CLIENT IDENTITY */}
      <section className="flex flex-col gap-4 rounded-2xl border bg-card p-5 shadow-ui-sm sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 flex-shrink-0">
            {companyLogo ? (
              <img src={companyLogo} alt={companyName} className="h-full w-full rounded-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center rounded-full bg-brand-navy text-lg font-semibold text-white">
                {getInitials(companyName)}
              </div>
            )}
          </div>
          <div className="min-w-0">
            <h2 className="truncate font-display text-lg font-bold text-brand-navy">{companyName}</h2>
            <p className="truncate text-sm text-muted-foreground">{companyEmail}</p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Category: {kyb?.category || 'N/A'}
              </span>
              <Status label={(kyb?.status || 'N/A').replace('-', ' ')} />
            </div>
          </div>
        </div>

      </section>

      {/* SUBMITTED DETAILS */}
      <section className="rounded-2xl border bg-card p-5 shadow-ui-sm">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-orange">
          Submission
        </div>
        <h2 className="mb-4 text-lg font-semibold text-brand-navy">Submitted Details</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          <DetailItem label="Legal Name" value={kyb?.legal_name} />
          <DetailItem label="Website URL" value={kyb?.website} />
          <DetailItem label="Identification Type" value={kyb?.identification_type} />
          <DetailItem label="Identification Number" value={kyb?.identification_number} />
          <DetailItem label="Source of Funds" value={kyb?.source_of_funds} />
          <DetailItem label="Industry" value={kyb?.industry} />
          <DetailItem label="Company Address" value={kyb?.company_address} />
          <DetailItem label="Category" value={kyb?.category} />
        </div>
      </section>

      {/* DOCUMENTS */}
      <section className="space-y-4">
        <div className="overflow-hidden rounded-2xl border bg-card shadow-ui-sm">
          <div className="flex items-center justify-between border-b px-4 py-4 sm:px-5">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-orange">
                Review
              </div>
              <h2 className="font-display text-lg font-semibold text-brand-navy">Documents</h2>
            </div>
            <span className="text-xs font-semibold tabular-nums text-muted-foreground">
              {approvedCount}/{totalCount} approved
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Document</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Preview</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {docs.map((docKey) => {
                  const config = DOC_LABELS[docKey];
                  const docUrl = kyb?.[config.key];
                  const docStatus = kyb?.[config.statusKey] || 'not-started';
                  const docReason = kyb?.[config.reasonKey];
                  const isRejected = docStatus === 'rejected';
                  const isApproved = docStatus === 'approved';
                  const isRejectingThis = rejectingKey === docKey;

                  return (
                    <tr key={docKey} className="transition-colors hover:bg-surface/60">
                      <td className="whitespace-nowrap px-4 py-3 font-semibold text-brand-navy">
                        {config.label}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <Status label={docStatus.replace('-', ' ')} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {docUrl ? (
                          <a
                            href={docUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-brand-orange hover:underline"
                          >
                            View
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not provided</span>
                        )}
                      </td>
                      <td className="max-w-[200px] whitespace-normal break-words px-4 py-3 text-xs">
                        {docReason ? (
                          <span className="text-red-700">{docReason}</span>
                        ) : (
                          <span className="text-muted-foreground">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isRejectingThis ? (
                          <div className="flex min-w-[200px] flex-col gap-2">
                            <textarea
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              rows={2}
                              className="resize-none rounded-md border border-input bg-transparent p-2 text-xs shadow-ui-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                              placeholder="Reason for rejection..."
                            />
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => handleRejectDoc(docKey)}
                                disabled={!rejectReason.trim()}
                                className="inline-flex h-8 items-center rounded-md bg-red-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:pointer-events-none disabled:opacity-50"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => { setRejectingKey(null); setRejectReason(''); }}
                                className="inline-flex h-8 items-center rounded-md border border-brand-navy px-3 text-xs font-semibold text-brand-navy transition-colors hover:bg-surface"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="relative z-10 flex flex-wrap gap-2">
                            <button
                              onClick={() => handleApproveDoc(docKey)}
                              /* Stays live on an approved document until the
                                 KYB itself is verified, so a record approved
                                 under the old two-step flow can be finished. */
                              disabled={isApproved && kybCompleted}
                              className="relative z-10 inline-flex h-8 items-center rounded-md bg-brand-navy px-3 text-xs font-semibold text-white transition-colors hover:bg-brand-navy-800 disabled:pointer-events-none disabled:opacity-50"
                            >
                              Approve
                            </button>
                            {!isApproved && (
                              <button
                                onClick={() => { setRejectingKey(docKey); setRejectReason(docReason || ''); }}
                                disabled={isRejected}
                                className="relative z-10 inline-flex h-8 items-center rounded-md border border-red-300 px-3 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50 disabled:pointer-events-none disabled:opacity-50"
                              >
                                Disapprove
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* REJECTION DETAILS */}
        {kyb?.status === 'rejected' && kyb?.admin_note && (
          <div className="overflow-hidden rounded-2xl border border-red-300 bg-card shadow-ui-sm">
            <button
              onClick={() => setShowAdminNote(!showAdminNote)}
              className="flex w-full items-center justify-between bg-red-50 px-4 py-3 transition-colors hover:bg-red-100"
            >
              <span className="text-sm font-semibold text-red-800">Rejection Details</span>
              {showAdminNote ? <FiChevronUp className="text-red-700" /> : <FiChevronDown className="text-red-700" />}
            </button>
            {showAdminNote && (
              <div className="border-t border-red-200 px-4 py-3">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Admin Note
                </p>
                <p className="text-sm text-red-800">{kyb.admin_note}</p>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
